/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Product URL slugs (UCR-3036).
 *
 * A **standard** product's id IS its SKU (`3353012`), so its PDP URL read as a bare number. Master
 * products already carry human-readable ids (`Living-Soil-Kit`), so only standard products need a
 * slug — the display name, lowercased and dash-joined: `/product/organic-microbes`.
 *
 * This module is deliberately pure and shared (not `.server.ts`): the canonical redirect runs on the
 * server and the tile links are built in the browser, and the two must agree character-for-character
 * or every tile click would bounce through a redirect. The reverse direction (slug → product id,
 * which needs SCAPI) lives in `slug.server.ts`.
 */

/** Unicode combining marks — what NFD splits off an accented character. */
const COMBINING_MARKS = /[\u0300-\u036f]/g;

/**
 * Turn a product display name into a URL path segment: lowercase, accents folded, every run of
 * non-alphanumerics collapsed to a single dash, no leading/trailing dashes.
 *
 * Returns `''` when the name yields nothing usable — empty/missing, or written entirely in a script
 * that folds away (e.g. Japanese). Callers treat `''` as "this product has no slug" and fall back to
 * its id, so a non-Latin catalog degrades to today's URLs instead of to `/product/-`.
 *
 * @example
 * slugifyProductName('Organic Microbes');         // => 'organic-microbes'
 * slugifyProductName('Café  &  Crème 2L');        // => 'cafe-creme-2l'
 * slugifyProductName('Living Soil Kit (6-Pack)'); // => 'living-soil-kit-6-pack'
 * slugifyProductName(undefined);                  // => ''
 */
export function slugifyProductName(name: string | undefined | null): string {
    if (!name) return '';
    return (
        name
            // NFD splits accented characters into base + combining mark so the mark can be dropped,
            // keeping "Crème" as "creme" rather than losing the whole letter to the dash rule below.
            // Deliberately NFD and not NFKD: the compatibility form expands symbols into letters, so
            // "Boost™" would slugify to "boosttm" instead of dropping the mark.
            .normalize('NFD')
            .replace(COMBINING_MARKS, '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
    );
}

/**
 * Whether a PDP path segment could be a slug produced by {@link slugifyProductName}.
 *
 * One-way test used as a fast path: slugs are always lowercase, so a segment carrying an uppercase
 * character is definitively an id (`Living-Soil-Kit`, `25752235M`) and never needs the slug→id
 * lookup. A lowercase segment may be either — a numeric SKU passes this test — so a `true` here only
 * means "worth looking up", never "is a slug".
 */
export function couldBeProductSlug(segment: string | undefined | null): boolean {
    return typeof segment === 'string' && segment.length > 0 && segment === segment.toLowerCase();
}
